import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import {
    Box,
    Grid,
    Paper,
    Skeleton,
    Tab,
    Tabs,
    Typography,
    useTheme,
} from "@mui/material";
import BankingWidgetSummary from "./banking-widget-summary";
import {
    useGetFiltersQuery,
    useGetStatsQuery,
    useGetPerformanceGraphQuery,
    useGetPerformanceSplitQuery,
} from "src/context/api/businessOverview/api";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import {
    setAutoTimeStep,
    setDatesForcedByActivation,
    setFilterCompare,
    setFilterDate,
    setFilterDateCompare,
    setFilterLoadedCountry,
    setFilterLoadedDevice,
    setFilterLoadedPlatform,
    setFilterMinDatePicker,
    setFilterTimestep,
} from "src/context/reducers/filter-settings/filterSlice";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useGetSinceActivationQuery } from "src/context/api/liveBidding/api";
import { differenceInCalendarDays, parseISO, subDays } from "date-fns";
import { fDate } from "src/utils/format-time";
import ChartPerformanceBusinessOverview from "src/sections/charts/chart-performance-business-overview";
import DataGridPerformanceTable from "src/components/data-grid/data-grid-performance-split-business-overview";

export default function BusinessOverview({
    disableTab,
    disableSinceActivation,
}: any) {
    const workspace: any = useCurrentWorkspace();
    const theme = useTheme();
    const dispatch = useDispatch();
    const filterStore = useSelector((state: any) => state.filters);

    const [value, setValue] = useState(0);
    const [loadedTabs, setLoadedTabs] = useState<Set<number>>(new Set([0]));
    const [hasInitializedTab, setHasInitializedTab] = useState(false);
    const [sinceActivate, setSinceActivate]: any = useState(null);

    // ------------------
    // Utils
    // ------------------
    const setLast30Days = useCallback(() => {
        const todayMinus1 = subDays(new Date(), 1);
        const start = subDays(todayMinus1, 29);
        dispatch(
            setFilterDate({
                start: fDate(start, "yyyy-MM-dd"),
                end: fDate(todayMinus1, "yyyy-MM-dd"),
            })
        );
    }, [dispatch]);

    // ------------------
    // Queries
    // ------------------
    const { data: filtersData } = useGetFiltersQuery(
        {
            workspaceId: workspace?.id,
            start_date: filterStore?.start,
            end_date: filterStore?.end,
            countries: filterStore?.countries,
            devices: filterStore?.devices,
            platforms: filterStore?.platforms,
        },
        { skip: !workspace?.id }
    );

    const { data: sinceActivationData } = useGetSinceActivationQuery(
        { workspaceId: workspace?.id },
        { skip: !workspace?.id }
    );

    const {
        data: statsData,
        error: statsError,
        isFetching: isFetchingStats,
    } = useGetStatsQuery(
        {
            workspaceId: workspace?.id,
            platforms: filterStore?.platforms,
            devices: filterStore?.devices,
            countries: filterStore?.countries,
            start_date: filterStore?.start,
            end_date: filterStore?.end,
            compare_start_date: filterStore?.compareStart,
            compare_end_date: filterStore?.compareEnd,
            compare: filterStore?.compare,
            savingsFormula: filterStore?.savingsFormulaFilter,
            timestep: filterStore?.timestep,
            sinceActivate: filterStore?.datesForcedByActivation,
        },
        { skip: sinceActivate === null }
    );

    const {
        data: performanceGraphData,
        isFetching: isFetchingGraph,
        error: performanceGraphError,
    } = useGetPerformanceGraphQuery(
        {
            workspaceId: workspace?.id,
            platforms: filterStore?.platforms,
            devices: filterStore?.devices,
            countries: filterStore?.countries,
            start_date: filterStore?.start,
            end_date: filterStore?.end,
            savingsFormula: filterStore?.savingsFormulaFilter,
            compare_start_date: filterStore?.compareStart,
            timestep: filterStore?.timestep,
            sinceActivate: filterStore?.datesForcedByActivation,
        },
        { skip: sinceActivate === null }
    );

    // ------------------
    // Effects
    // ------------------
    const hasInitializedActivation = useRef(false);

    useEffect(() => {
        if (filtersData) {
            dispatch(setFilterLoadedCountry(filtersData.countries || []));
            dispatch(setFilterLoadedDevice(filtersData.devices || []));
            dispatch(setFilterLoadedPlatform(filtersData.platforms || []));
            if (filtersData.min_date) {
                const d = new Date(filtersData.min_date);
                d.setFullYear(2023);
                dispatch(setFilterMinDatePicker(d.toUTCString()));
            }
        }
    }, [filtersData, dispatch]);

    useEffect(() => {
        if (hasInitializedActivation.current) return;

        if (disableSinceActivation) {
            hasInitializedActivation.current = true;
            setLast30Days();
            setSinceActivate(false);
            dispatch(setFilterCompare(false));
            dispatch(setDatesForcedByActivation(false));
            return;
        }

        if (!sinceActivationData) return;
        hasInitializedActivation.current = true;

        if (sinceActivationData?.first_entry_date) {
            const start = parseISO(sinceActivationData.first_entry_date);
            const end = subDays(new Date(), 1);

            dispatch(
                setFilterDate({
                    start: fDate(start, "yyyy-MM-dd"),
                    end: fDate(end, "yyyy-MM-dd"),
                })
            );

            const diffDays = differenceInCalendarDays(end, start) + 1;
            const compareEnd = subDays(start, 1);
            const compareStart = subDays(compareEnd, diffDays - 1);

            dispatch(
                setFilterDateCompare({
                    compareStart: fDate(compareStart, "yyyy-MM-dd"),
                    compareEnd: fDate(compareEnd, "yyyy-MM-dd"),
                })
            );
            dispatch(setFilterCompare(true));
            dispatch(setDatesForcedByActivation(true));
            setSinceActivate(true);
        } else {
            setLast30Days();
            setSinceActivate(false);
            dispatch(setFilterCompare(false));
            dispatch(setDatesForcedByActivation(false));
        }
    }, [sinceActivationData, disableSinceActivation, dispatch, setLast30Days]);

    const isCustom = workspace?.custom;

    const hasMultipleCountries = filterStore?.loadedCountries?.length > 1;
    const hasMultiplePlatforms = (filterStore?.loadedPlatform?.length || 0) >= 2;


    const getTabsOrder = useCallback(
        ({ country, platforms }: { country: boolean; platforms: boolean }) => {
            if (country && !platforms) return ["Countries", "Devices", "Date"];
            if (country && platforms) return ["Countries", "Platforms", "Devices", "Date"];
            if (!country && platforms) return ["Platforms", "Devices", "Date"];
            if (!country && !platforms) return ["Date", "Devices"];
            return ["Date"];
        },
        []
    );

    const tabsToShow = useMemo(() => {
        let tabs = getTabsOrder({
            country: hasMultipleCountries,
            platforms: hasMultiplePlatforms,
        });
        return isCustom ? tabs.filter((tab) => tab !== "Devices") : tabs;
    }, [hasMultipleCountries, hasMultiplePlatforms, isCustom, getTabsOrder]);



    useEffect(() => {
        if (!hasInitializedTab && hasMultipleCountries === false && tabsToShow.includes("Date")) {
            const index = tabsToShow.indexOf("Date");
            setValue(index);
            setLoadedTabs(new Set([index]));
            setHasInitializedTab(true);
        }
    }, [hasMultipleCountries, tabsToShow, hasInitializedTab]);

    const splitOptions = useMemo(() => {
        return tabsToShow.map((tab) =>
            tab === "Countries"
                ? "country"
                : tab === "Platforms"
                    ? "platform"
                    : tab === "Devices"
                        ? "device"
                        : "date"
        );
    }, [tabsToShow]);

    const handleChange = useCallback(
        (event: any, newValue: number) => {
            setValue(newValue);
            setLoadedTabs((prev) => new Set(prev).add(newValue));
        },
        []
    );

    // ------------------
    // PerformanceSplit Query (lazy load selon tab)
    // ------------------
    const {
        data: performanceSplitRows,
        isFetching: isFetchingSplitRows,
        error: performanceSplitRowsError,
    } = useGetPerformanceSplitQuery(
        {
            workspaceId: workspace?.id,
            platforms: filterStore?.platforms,
            devices: filterStore?.devices,
            countries: filterStore?.countries,
            start_date: filterStore?.start,
            end_date: filterStore?.end,
            compare_start_date: filterStore?.compareStart,
            bqTable: workspace?.bqTable,
            compare: filterStore?.compare,
            compare_end_date: filterStore?.compareEnd,
            savingsFormula: filterStore?.savingsFormulaFilter,
            timestep: filterStore?.timestep,
            split: splitOptions[value],
            sinceActivate: filterStore?.datesForcedByActivation,
        },
        {
            skip: sinceActivate === null || !loadedTabs.has(value),
        }
    );

    // ------------------
    // Stats cards
    // ------------------
    const calculateCompareData = useCallback(
        (current: number | undefined, previous: number | undefined): number => {
            if (!current || !previous) return 0;
            return ((current - previous) / previous) * 100;
        },
        []
    );

    const filteredStats: any = useMemo(() => {
        if (!statsData) return [];
        return [
            {
                title: t("dashboard.global.savings"),
                value: statsData?.bankingWidgetSummary?.savings_previous_month,
                compare: calculateCompareData(
                    statsData?.bankingWidgetSummary?.savings_previous_month,
                    statsData?.bankingWidgetSummary?.savings_previous_month_compare
                ),
                icon: "solar:hand-money-bold-duotone",
                color: "success",
                textColor: "#118d57",
                spanColor: theme.palette.mode === "dark" ? "#6dad8d" : "transparent",
                series:
                    statsData?.bankingWidgetSummary?.sparklines?.savings_previous_month?.map(
                        (item: any) => ({ y: item?.value, x: item?.date_day })
                    ),
            },
            {
                title: "Ad Cost with Cross-Brand",
                value: statsData?.bankingWidgetSummary?.ad_cost_with_cb,
                compare: calculateCompareData(
                    statsData?.bankingWidgetSummary?.ad_cost_with_cb,
                    statsData?.bankingWidgetSummary?.ad_cost_with_cb_compare
                ),
                icon: "solar:money-bag-bold-duotone",
                spanColor: theme.palette.mode === "dark" ? "#ffd066" : "transparent",
                color: "warning",
                invertedColor: true,
                textColor: "#b76e00",
                series:
                    statsData?.bankingWidgetSummary?.sparklines?.ad_cost_with_cb?.map(
                        (item: any) => ({ y: item?.value, x: item?.date_day })
                    ),
            },
            {
                title: "Ad Cost without Cross-Brand",
                value: statsData?.bankingWidgetSummary?.ad_cost_without_cb,
                compare: calculateCompareData(
                    statsData?.bankingWidgetSummary?.ad_cost_without_cb,
                    statsData?.bankingWidgetSummary?.ad_cost_without_cb_compare
                ),
                icon: "solar:money-bag-bold-duotone",
                color: "error",
                invertedColor: true,
                textColor: "#b71d18",
                spanColor: theme.palette.mode === "dark" ? "#db8377" : "transparent",
                series:
                    statsData?.bankingWidgetSummary?.sparklines?.ad_cost_without_cb?.map(
                        (item: any) => ({ y: item?.value, x: item?.date_day })
                    ),
            },
            {
                title: t("dashboard.brandTraffic.totalBrandTraffic"),
                value: statsData?.bankingWidgetSummary?.brand_traffic,
                compare: calculateCompareData(
                    statsData?.bankingWidgetSummary?.brand_traffic,
                    statsData?.bankingWidgetSummary?.brand_traffic_compare
                ),
                color: "secondary",
                textColor: "#880e4f",
                icon: "solar:user-circle-bold-duotone",
                spanColor: theme.palette.mode === "dark" ? "#a64b7b" : "transparent",
                series:
                    statsData?.bankingWidgetSummary?.sparklines?.brand_traffic?.map(
                        (item: any) => ({ y: item?.value, x: item?.date_day })
                    ),
            },
        ];
    }, [statsData, calculateCompareData, theme.palette.mode]);

    // ------------------
    // Render
    // ------------------
    const placeholders = [1, 2, 3, 4];

    useEffect(() => {
        if (workspace?.name.includes('Demo')) {
            dispatch(setAutoTimeStep(false))
            dispatch(setFilterTimestep('year_week'))
        } else {
            return
        }
    }, [workspace])
    
    return (
        <Box>
            {/* Stats Cards */}
            <Grid container spacing={2}>
                {(isFetchingStats || (!statsData && !statsError)
                    ? placeholders
                    : filteredStats
                ).map((stat: any, index: any) => (
                    <Grid item key={index} xs={6} md={4} lg={3}>
                        {isFetchingStats || (!statsData && !statsError) ? (
                            <Skeleton
                                width="100%"
                                height="138px"
                                style={{ borderRadius: 10 }}
                                variant="rounded"
                                animation="wave"
                            />
                        ) : (
                            <BankingWidgetSummary
                                spanColor={stat.spanColor}
                                title={stat?.title}
                                compareData={stat?.compare}
                                textColor={stat?.textColor}
                                icon={stat?.icon}
                                color={stat.color}
                                invertedColor={stat?.invertedColor}
                                percent={0}
                                total={stat?.value}
                                chart={{ series: stat.series ?? [] }}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>

            {/* Graph */}
            <Paper
                sx={{
                    backgroundColor: theme.palette.mode === "dark" ? "#212b36" : "white",
                    boxShadow:
                        "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)",
                    height: "500px",
                    borderRadius: 2,
                    mt: 2,
                    mb: 1,
                }}
            >
                {isFetchingGraph || (!performanceGraphData && !performanceGraphError) ? (
                    <Skeleton
                        width="100%"
                        height="100%"
                        variant="rectangular"
                        animation="wave"
                        style={{ borderRadius: 10 }}
                    />
                ) : (
                    <Box p={3} height="100%">
                        <Typography variant="overline" color="textSecondary" sx={{ mb: 1 }}>
                            Impact of Cross-Brand
                        </Typography>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Ad Spend Saved on Brand Traffic (Before & After)
                        </Typography>
                        <Box sx={{ height: 370 }}>
                            <ChartPerformanceBusinessOverview
                                data={performanceGraphData?.performanceGraph || []}
                            />
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* DataGrid */}
            <Paper
                sx={{
                    backgroundColor: theme.palette.mode === "dark" ? "#212b36" : "white",
                    boxShadow:
                        "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)",
                    borderRadius: 2,
                    mt: 2,
                    mb: 1,
                }}
            >
                {isFetchingSplitRows ||
                    (!performanceSplitRows && !performanceSplitRowsError) ? (
                    <Skeleton
                        width="100%"
                        height={400}
                        variant="rectangular"
                        animation="wave"
                        style={{ borderRadius: 10 }}
                    />
                ) : (
                    <Box>
                        {!disableTab && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    px: 4,
                                    pt: 2,
                                    mb: 1,
                                }}
                            >
                                <Tabs value={value} onChange={handleChange}>
                                    {tabsToShow.map((tabLabel) => (
                                        <Tab key={tabLabel} label={tabLabel} />
                                    ))}
                                </Tabs>
                            </Box>
                        )}

                        {performanceSplitRows?.performanceSplit?.some(
                            (item: any) => item.split
                        ) && (
                                <DataGridPerformanceTable
                                    data={performanceSplitRows.performanceSplit}
                                    split={splitOptions[value]}
                                    compare={filterStore?.compare}
                                    sinceCrossBrand={sinceActivationData?.first_entry_date}
                                />
                            )}
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
